<template>
  <div class="mobile-custom-lists-page">
    <header v-if="!showingArchived" class="mobile-lists-bar">
      <button
        type="button"
        class="mobile-lists-action"
        :title="$t('ui.newCustomList')"
        :aria-label="$t('ui.newCustomList')"
        @click="onNewList"
      >
        <i class="bi bi-clipboard-plus"></i>
      </button>
      <button
        type="button"
        class="mobile-lists-action"
        :title="$t('ui.reorderCustomLists')"
        :aria-label="$t('ui.reorderCustomLists')"
        data-bs-toggle="modal"
        data-bs-target="#ReorderCustomListsModal"
      >
        <i class="bi bi-arrow-left-right"></i>
      </button>
      <div v-if="activeLists.length" class="mobile-lists-chips" role="tablist">
        <button
          v-for="(list, index) in activeLists"
          :key="list.listId"
          type="button"
          role="tab"
          class="mobile-lists-chip"
          :class="{ active: list.listId === activeListId }"
          :aria-selected="list.listId === activeListId"
          @click="activeListId = list.listId"
        >
          {{ chipLabel(list, index) }}
        </button>
      </div>
      <button
        type="button"
        class="mobile-lists-action mobile-lists-archived-btn"
        :title="$t('ui.archivedLists')"
        :aria-label="$t('ui.archivedLists')"
        @click="showingArchived = true"
      >
        <i class="bi bi-archive"></i>
        <span v-if="archivedLists.length" class="mobile-lists-archived-badge">{{
          archivedLists.length
        }}</span>
      </button>
    </header>

    <header v-else class="mobile-lists-bar">
      <button
        type="button"
        class="mobile-lists-action"
        :title="$t('ui.goBack')"
        :aria-label="$t('ui.goBack')"
        @click="showingArchived = false"
      >
        <i class="bi bi-chevron-left"></i>
      </button>
      <div class="mobile-lists-archived-title">{{ $t("ui.archivedLists") }}</div>
    </header>

    <div class="mobile-lists-body">
      <template v-if="showingArchived">
        <div v-if="!archivedLists.length" class="mobile-lists-empty">
          <p>{{ $t("ui.archivedLists") }}</p>
        </div>
        <ul v-else class="mobile-archived-list">
          <li v-for="list in archivedLists" :key="list.listId" class="mobile-archived-row">
            <button type="button" class="mobile-archived-name" @click="openArchivedList(list.listId)">
              {{ chipLabel(list, 0) }}
            </button>
            <div class="mobile-archived-actions">
              <button type="button" class="mobile-archived-action" @click="openArchivedList(list.listId)">
                {{ $t("ui.openList") }}
              </button>
              <button type="button" class="mobile-archived-action" @click="onRestore(list.listId)">
                {{ $t("ui.restoreList") }}
              </button>
              <button
                type="button"
                class="mobile-archived-action danger"
                @click="onDeleteArchived(list)"
              >
                {{ $t("ui.removeList") }}
              </button>
            </div>
          </li>
        </ul>
      </template>
      <template v-else>
        <div v-if="!activeLists.length" class="mobile-lists-empty">
          <p>{{ $t("settings.customLists") }}</p>
          <button type="button" class="mobile-lists-empty-cta" @click="onNewList">
            {{ $t("ui.newCustomList") }}
          </button>
        </div>
        <to-do-list
          v-else-if="activeListId != null && activeListIndex >= 0 && activeListReady"
          :key="activeListId"
          :id="activeListId"
          :customTodoList="true"
          :showCustomList="true"
          :cTodoListIndex="activeListIndex"
          mobile-layout
        />
      </template>
    </div>
  </div>
</template>

<script>
import toDoList from "../components/toDoList";
import {
  createCustomTodoList,
  restoreCustomTodoList,
} from "../helpers/customListActions.js";
const {
  activeCustomLists,
  archivedCustomLists,
} = require("../helpers/customListArchive.js");
import { ensureCustomListLoaded } from "../sync/sessionSync.js";
import { createNote } from "../sync/engine";

export default {
  name: "MobileCustomListsPage",
  components: {
    toDoList,
  },
  data() {
    return {
      activeListId: null,
      activeListReady: false,
      listLoadError: "",
      showingArchived: false,
    };
  },
  computed: {
    cTodoListIds() {
      return this.$store.getters.cTodoListIds || [];
    },
    activeLists() {
      return activeCustomLists(this.cTodoListIds);
    },
    archivedLists() {
      return archivedCustomLists(this.cTodoListIds);
    },
    activeListIndex() {
      if (!this.activeListId) return -1;
      return this.cTodoListIds.findIndex((list) => list.listId === this.activeListId);
    },
  },
  watch: {
    cTodoListIds: {
      immediate: true,
      handler() {
        this.ensureActiveList();
      },
    },
    activeListId: {
      immediate: true,
      handler(listId) {
        this.loadActiveListFromServer(listId);
      },
    },
  },
  methods: {
    chipLabel(list, index) {
      const name = list && list.listName ? String(list.listName).trim() : "";
      return name || `#${index + 1}`;
    },
    ensureActiveList() {
      const active = this.activeLists;
      if (!active.length) {
        // Keep showing an archived list if user opened one from archive
        const still =
          this.activeListId &&
          this.cTodoListIds.some((l) => l.listId === this.activeListId);
        if (!still) this.activeListId = null;
        return;
      }
      const stillActive = active.some((list) => list.listId === this.activeListId);
      if (!stillActive) {
        this.activeListId = active[0].listId;
      }
    },
    onNewList() {
      this.showingArchived = false;
      const created = createCustomTodoList(this.$store);
      this.activeListId = created.listId;
    },
    openArchivedList(listId) {
      this.showingArchived = false;
      this.activeListId = listId;
    },
    onRestore(listId) {
      restoreCustomTodoList(this.$store, listId, {
        t: this.$t.bind(this),
        createNoteFn: createNote,
      });
      this.showingArchived = false;
      this.activeListId = listId;
    },
    onDeleteArchived(list) {
      const index = this.cTodoListIds.findIndex((l) => l.listId === list.listId);
      if (index < 0) return;
      this.$store.commit("actionsCListToRmvUpdate", {
        index,
        name: list.listName,
        id: list.listId,
      });
      const modal = document.getElementById("customListRemoveModal");
      if (modal && window.bootstrap && window.bootstrap.Modal) {
        window.bootstrap.Modal.getOrCreateInstance(modal).show();
      } else if (modal) {
        // fallback: trigger via data API
        const btn = document.createElement("button");
        btn.setAttribute("data-bs-toggle", "modal");
        btn.setAttribute("data-bs-target", "#customListRemoveModal");
        document.body.appendChild(btn);
        btn.click();
        btn.remove();
      }
    },
    async loadActiveListFromServer(listId) {
      if (listId == null) {
        this.activeListReady = false;
        return;
      }
      this.activeListReady = false;
      this.listLoadError = "";
      try {
        await ensureCustomListLoaded(this.$store, listId);
        this.activeListReady = true;
      } catch (error) {
        this.listLoadError = error && error.message ? error.message : "请求失败";
        console.error("custom list load failed", listId, error);
      }
    },
  },
};
</script>

<style scoped>
.mobile-custom-lists-page {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  height: 100%;
}

.mobile-lists-bar {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 12px;
  border-bottom: 1px solid var(--m-hairline, rgba(60, 60, 67, 0.18));
  flex-shrink: 0;
  min-width: 0;
  background: var(--m-surface, #fff);
}

.mobile-lists-action {
  border: none;
  background: transparent;
  color: #0d6efd;
  padding: 0;
  min-width: 40px;
  min-height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 22px;
  flex-shrink: 0;
  border-radius: 10px;
  position: relative;
}

.dark-theme .mobile-lists-action {
  color: #0a84ff;
}

.mobile-lists-archived-btn {
  margin-left: auto;
}

.mobile-lists-archived-badge {
  position: absolute;
  top: 4px;
  right: 2px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 999px;
  background: #0d6efd;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  line-height: 16px;
  text-align: center;
}

.dark-theme .mobile-lists-archived-badge {
  background: #0a84ff;
}

.mobile-lists-archived-title {
  flex: 1;
  font-size: var(--m-bar, 17px);
  font-weight: 650;
  text-align: center;
  padding-right: 40px;
}

.mobile-lists-chips {
  display: flex;
  flex: 1;
  min-width: 0;
  overflow-x: auto;
  gap: 8px;
  padding: 2px 0;
  -webkit-overflow-scrolling: touch;
}

.mobile-lists-chip {
  flex-shrink: 0;
  border: none;
  background: rgba(120, 120, 128, 0.12);
  color: inherit;
  border-radius: 999px;
  padding: 8px 14px;
  font-size: var(--m-caption, 13px);
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  max-width: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.mobile-lists-chip.active {
  font-weight: 650;
  background: rgba(13, 110, 253, 0.14);
  color: #0d6efd;
}

.dark-theme .mobile-lists-chip.active {
  background: rgba(10, 132, 255, 0.28);
  color: #0a84ff;
}

.mobile-lists-body {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  padding: 8px 12px 12px;
  background: var(--m-page, #f2f2f7);
}

.mobile-lists-body :deep(.to-do-list-container) {
  flex: 1 1 auto !important;
  width: 100%;
  max-width: 100%;
  height: 100%;
  min-height: 0;
  display: flex !important;
  flex-direction: column;
  overflow: hidden;
  background: var(--m-surface, #fff);
  border-radius: var(--m-radius, 12px);
  padding: 12px 12px 6px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.mobile-archived-list {
  list-style: none;
  margin: 0;
  padding: 0;
  overflow: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.mobile-archived-row {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  background: var(--m-surface, #fff);
  border-radius: 12px;
}

.mobile-archived-name {
  border: none;
  background: transparent;
  color: inherit;
  text-align: left;
  font-size: var(--m-text-secondary, 15px);
  font-weight: 650;
  padding: 0;
  cursor: pointer;
}

.mobile-archived-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.mobile-archived-action {
  border: none;
  background: rgba(120, 120, 128, 0.12);
  color: #0d6efd;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.mobile-archived-action.danger {
  color: #dc3545;
}

.dark-theme .mobile-archived-action {
  color: #0a84ff;
}

@media (min-width: 768px) {
  .mobile-lists-body {
    padding: 16px 24px 24px;
  }

  .mobile-lists-body :deep(.to-do-list-container) {
    max-width: 720px;
    margin: 0 auto;
  }
}

.mobile-lists-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  padding: 48px 24px;
  text-align: center;
  font-size: var(--m-text-secondary, 15px);
  color: var(--m-muted, rgba(60, 60, 67, 0.6));
}

.mobile-lists-empty-cta {
  border: none;
  background: #0d6efd;
  color: #fff;
  border-radius: 12px;
  padding: 12px 18px;
  font-size: var(--m-text-secondary, 15px);
  font-weight: 600;
  cursor: pointer;
}

.dark-theme .mobile-lists-empty-cta {
  background: #0a84ff;
}
</style>
